import { Button, Stack, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { RouteConstants } from '../../navigation/navigation-types'
import ClientTable from '../../components/client/ClientTable'
import { ClientBriefDto, ClientType } from '../../services/api/sharedTypes/Client'
import ClientTypeSelect from '../../components/client/ClientTypeSelect'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import TextSearchField from '../../components/common/TextSearchField'
import DateRangePicker from '../../components/common/DateRangePicker'
import { ClientManager } from '../../services/api/ClientManager'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { useAppDispatch } from '../../hooks/reduxHooks'
import CustomContainer from '../../components/common/CustomContainer'
import { convertToSearchString } from '../../utils/global-utils'
import LinkWrapper from '../../components/common/LinkWrapper'
import CancelFiltersButton from '../../components/common/CancelFiltersButton'

const Clients: React.FC = () => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [selectedClientTypes, setSelectedClientTypes] = useState<ClientType[]>([])
    const [dateFrom, setDateFrom] = useState<Date | null>(null)
    const [dateTo, setDateTo] = useState<Date | null>(null)
    const [loadingData, setLoadingData] = useState<boolean>(true)
    const [searchedValue, setSearchedValue] = useState<string>('')
    const [clients, setClients] = useState<ClientBriefDto[]>()
    const [filteredClients, setFilteredClients] = useState<ClientBriefDto[]>()

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)
            const clientManager = ClientManager.getManager()
            try {
                const clientsRes = await clientManager.getAllClientsAsync()
                if (clientsRes) {
                    setClients(clientsRes.clients)
                    setFilteredClients(clientsRes.clients)
                }
            } catch (error) {
                setLoadingData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }
            setLoadingData(false)
        }

        fetchData()
    }, [setLoadingData])

    useEffect(() => {
        const setFilteredClientsByEventDate = async () => {
            if (!clients || clients.length <= 0) return
            setLoadingData(true)
            const clientManager = ClientManager.getManager()
            if (dateTo && dateFrom) {
                try {
                    if (isNaN(dateTo.getTime()) || isNaN(dateFrom.getTime())) return setLoadingData(false)
                    const clientsRes = await clientManager?.getAllFilteredClientsAsync(
                        dateFrom.toISOString(),
                        dateTo.toISOString()
                    )
                    if (clientsRes) {
                        setFilteredClients(clientsRes.clients)
                    }
                } catch (error) {
                    setLoadingData(false)
                    return dispatch(displayMessage({ message: error, type: 'error' }))
                }
            } else {
                const clientsRes = await clientManager.getAllClientsAsync()
                if (clientsRes) {
                    setClients(clientsRes.clients)
                    setFilteredClients(clientsRes.clients)
                }
            }
            setLoadingData(false)
        }

        setFilteredClientsByEventDate()
    }, [dateFrom, dateTo])

    const removeClient = async (id: string) => {
        if (!clients) return

        const clientManager = ClientManager.getManager()
        try {
            await clientManager.deleteClientAsync(id)
            const newClients = clients.filter((client) => client.id !== id)
            if (newClients) {
                setClients(newClients)
                setFilteredClients(newClients)
            }
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        dispatch(displayMessage({ message: 'Klient smazán', type: 'success' }))
    }

    const clientForTable = filteredClients
        ?.filter((client: ClientBriefDto) => {
            return (
                convertToSearchString(client.name).includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(client.note).includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(client.companyRegistrationNumber).includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(client.valueAddedTaxIdentificationNumber).includes(convertToSearchString(searchedValue)) ||
                client.contacts.some(
                    (contact) =>
                        convertToSearchString(contact.name).includes(convertToSearchString(searchedValue)) ||
                        convertToSearchString(contact.email).includes(convertToSearchString(searchedValue)) ||
                        convertToSearchString(contact.phone).includes(convertToSearchString(searchedValue))
                )
            )
        })
        .filter((client: ClientBriefDto) => {
            return selectedClientTypes.length === 0 || selectedClientTypes.includes(client.clientType)
        })

    if (loadingData) return <ScreenLoadingPlaceholder />

    return (
        <CustomContainer>
            <Stack justifyContent='space-between' direction='row'>
                <Typography variant='h5'>Klienti</Typography>
                <LinkWrapper route={RouteConstants.addClient}>
                    <Button variant='contained'>+ Přidat klienta</Button>
                </LinkWrapper>
            </Stack>

            <Stack mt={theme.spacing(1)} mb={theme.spacing(3)} direction='row' spacing={theme.spacing(3)}>
                <div style={{ width: 162, paddingTop: theme.spacing(1) }}>
                    <ClientTypeSelect
                        selectedClientTypes={selectedClientTypes}
                        onSelectedClientTypesChange={setSelectedClientTypes}
                    />
                </div>

                <DateRangePicker
                    labelFrom='Datum od'
                    labelTo='Datum do'
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onSetDateFrom={setDateFrom}
                    onSetDateTo={setDateTo}
                />

                <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value.toLowerCase())} />

                {(selectedClientTypes.length > 0 || searchedValue || dateFrom || dateTo) && (
                    <CancelFiltersButton onClick={
                        () => {
                            setSelectedClientTypes([])
                            setDateFrom(null)
                            setDateTo(null)
                            setSearchedValue('')
                        }
                    } />
                )}
            </Stack>

            {clients && clients.length > 0 ? (
                <>
                    <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                        {clientForTable?.length ?? 0} záznamů
                    </Typography>
                    <ClientTable clients={clientForTable ? clientForTable : clients} onRemoveClient={removeClient} />
                </>
            ) : (
                <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                    Žádné záznamy
                </Typography>
            )}
        </CustomContainer>
    )
}

export default Clients
